import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaSync,
  FaTicketAlt,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useAuth } from "../Components/StoredDetails";
import "../All CSS/Servicedashboard.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ServiceDashboard = () => {
  const { authData, setNotificationCount } = useAuth();

  const selectedLocation = authData?.selectedLocation || {};
  const compCode =
    selectedLocation.CompCode || authData?.user?.CompCode || "";
  const location =
    selectedLocation.CompLocation || authData?.user?.CompLocation || "";

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const loadTickets = async () => {
    try {
      setLoading(true);

      const { data } = await axios.post(
        `${API_BASE_URL}/api/support/tickets`
      );

      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const handleScanMail = async () => {
    try {
      setSyncing(true);

      await axios.post(`${API_BASE_URL}/api/support/scan-mail`, {
        compCode,
        location,
      });

      loadTickets();
    } catch (err) {
      console.error(err);
    } finally {
      setSyncing(false);
    }
  };

  const stats = useMemo(() => {
    const total = tickets.length;

    const newTickets = tickets.filter(
      (t) => (t.TicketStatus || "").toUpperCase() === "NEW"
    ).length;

    const open = tickets.filter(
      (t) => (t.TicketStatus || "").toUpperCase() === "OPEN"
    ).length;

    const close = tickets.filter(
      (t) => (t.TicketStatus || "").toUpperCase() === "CLOSE"
    ).length;

    return {
      total,
      new: newTickets,
      open,
      close,
    };
  }, [tickets]);

  useEffect(() => {
    setNotificationCount(stats.new);
  }, [stats.new, setNotificationCount]);

  const formatDate = (date) => {
    if (!date) return "-";

    const d = new Date(date);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const formatTime = (time) => {
    if (!time) return "-";

    const d = new Date(time);

    return d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC", // Prevent timezone conversion
    });
  };

  const groupedTickets = useMemo(() => {
    return {
      New: tickets.filter(
        (t) => (t.TicketStatus || "").toUpperCase() === "NEW"
      ),

      Open: tickets.filter(
        (t) => (t.TicketStatus || "").toUpperCase() === "OPEN"
      ),

      Close: tickets.filter(
        (t) => (t.TicketStatus || "").toUpperCase() === "CLOSE"
      ),
    };
  }, [tickets]);

  return (
    <div className="support-dashboard">

      {/* Cards */}
      <div className="status-grid">
        <div className="status-card-blue">
          <div className="status-title">New Tickets</div>
          <div className="status-value">{stats.new}</div>
          <div className="status-icon">
            <FaTicketAlt />
          </div>
        </div>

        <div className="status-card-gray">
          <div className="status-title">Open Tickets</div>
          <div className="status-value">{stats.open}</div>
          <div className="status-icon">
            <FaSpinner />
          </div>
        </div>

        <div className="status-card-red">
          <div className="status-title">Close Tickets</div>
          <div className="status-value">{stats.close}</div>
          <div className="status-icon">
            <FaCheckCircle />
          </div>
        </div>

        <div className="status-card-green">
          <div className="status-title">Total Tickets</div>
          <div className="status-value">{stats.total}</div>
          <div className="status-icon">
            <FaExclamationTriangle />
          </div>
        </div>

        <div className="Scan-Button-Container">
          <button
            className="scan-btn"
            onClick={handleScanMail}
            disabled={syncing}
          >
            <FaSync className={syncing ? "rotate" : ""} />
            {syncing ? "Scanning..." : "Scan Mailbox"}
          </button>
        </div>
      </div>


      <div className="ticket-board">
        {loading ? (
          <div className="loading-container">
            <FaSpinner className="loading-spinner" />
            <h3>Loading Tickets...</h3>
            <p>Please wait while we fetch the latest support tickets.</p>
          </div>
        ) : (
          Object.entries(groupedTickets).map(([status, list]) => (
            <div className="ticket-column" key={status}>
              <div className={`column-header ${status.toLowerCase()}`}>
                <span>{status}</span>
                <span className="ticket-count">{list.length}</span>
              </div>

              <div className="ticket-list">
                {list.length > 0 ? (
                  list.map((ticket, index) => (
                    <div className="ticket-card" key={ticket.RemoteID || index}>
                      {/* Header */}
                      <div className="ticket-header">
                        <div className="ticket-id-box">
                          <span className={`status-dot ${(ticket.TicketStatus || "").toLowerCase()}`}></span>

                          <span className="ticket-id-text">
                            Ticket ID: <strong>{ticket.RemoteID}</strong>
                          </span>
                        </div>

                        <span className={`ticket-status ${(ticket.TicketStatus || "").toLowerCase()}`}>
                          {ticket.TicketStatus}
                        </span>
                      </div>

                      {/* Title */}
                      <div className="ticket-body">

                        <h4 className="ticket-title">
                          {ticket.TicketDetails}
                        </h4>
                      </div>

                      {/* Footer */}
                      <div className="ticket-bottom">

                          <div className="meta-item">
                            <span>Type</span>
                            <strong>{ticket.TicketGroup}</strong>
                          </div>

                          <div className="meta-item">
                            <span>Channel</span>
                            <strong>Email</strong>
                          </div>

                      </div>

                      <div className="ticket-date">
                        <span>{formatDate(ticket.TicketDate)}</span>
                        <span>{formatTime(ticket.TicketTime)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-ticket-state">
                    <div className="empty-ticket-icon">📭</div>
                    <h4>No {status} Tickets</h4>
                    <p>
                      There are currently no{" "}
                      <strong>{status.toLowerCase()}</strong> tickets available.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

    </div>
  );
};

export default ServiceDashboard;